import React, { useState, useEffect, useContext, createContext } from 'react'
import { useLocalStorage } from 'react-use'

import { getLocationInfo } from 'src/api/getLocationInfo'

import { locationService } from 'src/utils/LocationService'

export type LocationInfo = {
  country: string | undefined
  state: string | undefined
  location: string | undefined
}

const defaults = {
  country: undefined,
  state: undefined,
  location: undefined
}

export const LocationContext = createContext<LocationInfo>(defaults)

const LOCATION_CACHE_TTL = 1000 * 60 * 60 * 24 // 1 day
const LOCATION_CACHE_KEY = 'asktia-preferred-location'

export const LocationProvider: React.FC<{ children: React.ReactNode[] }> = ({
  children
}) => {
  const [location, setLocation] = useLocalStorage<CachedLocation>(LOCATION_CACHE_KEY, undefined, {
    raw: false,
    serializer: JSON.stringify,
    deserializer: JSON.parse,
  })

  const [region, setRegion] = useState<Omit<LocationInfo, 'location'>>({
    country: location?.country,
    state: location?.state,
  })

  useEffect(() => {
    /**
     * If we have a cached location, use it.
     */
    if (location && location.created && location.created + LOCATION_CACHE_TTL > Date.now()) {
      return
    }

    if (!region.country) return /* no country */

    /**
     * If the country is not US, we don't need to do anything
     */
    if ('US'.localeCompare(region.country, 'en', { sensitivity: 'base' }) !== 0) {
      return
    }

    if (!region.state) return /* no state */

    const fetchLocationInfo = async (country: string, state: string) => {

      let proposedState = null
      // non production environments allow to specify the target state for testing purposes
      if (process.env.GATSBY_ENVIRONMENT !== 'production') {
        const urlParams = new URLSearchParams(window.location.search)
        proposedState = urlParams.has('state') ? urlParams.get('state') : null
      }

      const result = { location: proposedState ? proposedState : state }

      /**
       * If the state is one of the multi clinic states, we need to get the location info
       */
      const targetState = ['NY', 'CA'].find(
        (item) => item.localeCompare(result.location, 'en', { sensitivity: 'base' }) === 0
      )
      if ('string' === typeof targetState) {
        const response = await getLocationInfo(proposedState)
        result.location = response.success ? response.data.location : result.location
      }

      /**
       * If the state is AZ, we need to set the location to Phoenix/Scottsdale
       */
      if ('AZ'.localeCompare(state, 'en', { sensitivity: 'base' }) === 0) {
        result.location = 'Phoenix/Scottsdale'
      }

      const location = result.location.toLowerCase()

      setLocation({ country, state, location, created: Date.now() })
    }

    fetchLocationInfo(region.country, region.state)
  }, [region])

  useEffect(() => {
    const fetchRegionInfo = async () => {
      /**
       * If we have a cached location, use it.
       */
      if (location && location.country && location.state) {
        const expiration = location.created + LOCATION_CACHE_TTL
        const current = expiration < Date.now() ? undefined: location.location

        if ('undefined' === current) {
          /**
           * set the region so the effect triggers to perform the
           * location fetch and store the location in cache
           */
          setRegion({ country: location.country, state: location.state })
        }

        return
      }

      try {
        const { country, state } = await locationService.getLocation()

        if (!country || !state) return

        setRegion({ country, state })
      } catch (error) {
        console.error('Error checking location:', error)
      }
    }

    fetchRegionInfo()
  }, [])

  return (
    <LocationContext.Provider value={location as LocationInfo}>
      {children}
    </LocationContext.Provider>
  )
}

export const useLocation = () => {
  const { country, state, location } = useContext(LocationContext)
  return { country, state, location }
}

interface CachedLocation extends LocationInfo {
  created: number
}
